<script lang="ts" setup>
import {
  HealthState,
  type MonitoringState,
  MonitoringTarget,
} from 'gpcore-grpc/gpcore/type/v1/monitoring_pb';
import { computed } from 'vue';
import enumToGpDropdownData from 'gpcore-apikit/lib/enumToGpDropdownData';
import { GpAlert, GpTooltip } from '@gportal/uikit';
import SpecSheet from './new/specs/SpecSheet.vue';
import SpecSheetItem from './new/specs/SpecSheetItem.vue';
import Status from './Component.Status.vue';

const { state } = defineProps({
  state: {
    required: false,
    default: null,
    type: Object as () => MonitoringState,
  },
  showAlerts: {
    required: false,
    type: Boolean as () => boolean,
    default: true,
  },
});

interface Monitoring {
  label: string;
  target: MonitoringTarget;
  health: HealthState;
}

const components = computed(() => {
  return enumToGpDropdownData(MonitoringTarget).map((target) => {
    const status: Monitoring = {
      label: target.label,
      target: target.key,
      health: HealthState.UNSPECIFIED,
    };

    if (state && state.components.length > 0) {
      status.health =
        state?.components.find((c) => c.target === target.key)?.heathState ??
        HealthState.UNSPECIFIED;
    }
    return status;
  });
});

const hasUnknowns = computed(() => {
  return components.value.some((c) => c.health === HealthState.UNSPECIFIED);
});

const redfishUnavailable = computed(() => {
  const redfishState =
    components.value.find((c) => c.target === MonitoringTarget.REDFISH)?.health ??
    HealthState.UNSPECIFIED;

  return redfishState === HealthState.WARNING || redfishState === HealthState.CRITICAL;
});
</script>

<template>
  <GpAlert v-if="showAlerts && redfishUnavailable" alert-type="warning" class="tw-mb-5">
    <p>
      <strong>Warning:</strong> The node control plane seems to be unavailable. During the
      unavailability, the node might not be able to perform certain actions. The node itself might
      still be operational.
    </p>
    <p>
      Please contact support if the issue persists. We apologize for any inconvenience this may
      cause.
    </p>
  </GpAlert>
  <GpAlert v-if="showAlerts && hasUnknowns" class="tw-mb-5">
    <p>
      <strong>Notice:</strong> Please be advised that certain monitoring values do not provide a
      state for some time. This is due to hardware platform limitations or ongoing tasks on the
      hardware itself. This state does not necessarily indicate an issue. Instead, it reflects the
      current availability of the platform management system.
    </p>
  </GpAlert>
  <SpecSheet>
    <SpecSheetItem v-for="target in components" :key="target.label" :label="target.label">
      <template v-if="target.health === HealthState.UNSPECIFIED">
        <GpTooltip placement="top" content="No monitoring data available yet">
          <Status state="info" label="-" />
        </GpTooltip>
      </template>
      <template v-else-if="target.health === HealthState.WARNING">
        <Status state="warning" label="Warning" />
      </template>
      <template v-else-if="target.health === HealthState.CRITICAL">
        <Status state="danger" label="Critical" />
      </template>
      <template v-else>
        <Status state="success" label="Healthy" />
      </template>
    </SpecSheetItem>
  </SpecSheet>
</template>
